import { 
  getElementDetail,
  getAllElementsOrderedDesc,
  getElementsForFirstPage,
  getElementsForOtherPages,
  getLastEvents,
  getAllElementsNumber
} from '../../data/FirebaseService.js';
import { ElementType } from '../../data/ElementType.js';

/**
 * Repository for managing all Event-related data
 * Provides methods to fetch events, event details, and related events
 */
class EventRepository {
  
  /**
   * Fetch a specific event by ID
   * @param {number} id - Event ID
   * @returns {Promise<Object>} Event object
   */
  async getEventById(id) {
    try {
      const eventId = parseInt(id);
      if (isNaN(eventId)) {
        throw new Error('Invalid event ID');
      }

      const event = await getElementDetail(ElementType.EventType, eventId);
      
      if (!event) {
        throw new Error('Event not found');
      }

      return event;
    } catch (error) {
      console.error(`Error fetching event ${id}:`, error);
      throw new Error('Failed to fetch event');
    }
  }

  /**
   * Fetch all events ordered by most recent
   * @returns {Promise<Array>} Array of event objects
   */
  async getAllEvents() {
    try {
      const events = await getAllElementsOrderedDesc(ElementType.EventType);
      return events || [];
    } catch (error) {
      console.error('Error fetching all events:', error);
      throw new Error('Failed to fetch events');
    }
  }

  /**
   * Fetch the latest N events sorted by date (most recent first)
   * @param {number} limit - Number of events to fetch (default: 6)
   * @returns {Promise<Array>} Array of recent event objects sorted by date
   */
  async getLatestEvents(limit = 6) {
    try {
      // Fetch more events than needed to ensure we get the most recent by date
      // Since Firebase orders by ID, not date
      const fetchLimit = Math.max(limit * 2, 20);
      const events = await getLastEvents(fetchLimit);
      
      if (!events || events.length === 0) {
        return [];
      }
      
      // Sort events by date (most recent first)
      const sortedEvents = events.sort((a, b) => {
        const [dayA, monthA, yearA] = a.date.split('/').map(Number);
        const [dayB, monthB, yearB] = b.date.split('/').map(Number);
        
        // Compare year first, then month, then day
        if (yearB !== yearA) return yearB - yearA;
        if (monthB !== monthA) return monthB - monthA;
        return dayB - dayA;
      });
      
      // Return only the requested number of events
      return sortedEvents.slice(0, limit);
    } catch (error) {
      console.error('Error fetching latest events:', error);
      throw new Error('Failed to fetch latest events');
    }
  }

  /**
   * Fetch events for the first page (paginated)
   * @returns {Promise<Array>} Array of event objects (max 12)
   */
  async getEventsFirstPage() {
    try {
      const events = await getElementsForFirstPage(ElementType.EventType);
      return events || [];
    } catch (error) {
      console.error('Error fetching first page events:', error);
      throw new Error('Failed to fetch events');
    }
  }

  /**
   * Fetch events for a specific page (paginated)
   * @param {number} pageNumber - Page number (0-indexed)
   * @returns {Promise<Array>} Array of event objects (max 10)
   */
  async getEventsForPage(pageNumber) {
    try {
      if (pageNumber === 0) {
        return await this.getEventsFirstPage();
      }

      const totalEvents = await this.getTotalEventsCount();
      const events = await getElementsForOtherPages(
        ElementType.EventType,
        totalEvents,
        pageNumber
      );
      
      return events || [];
    } catch (error) {
      console.error(`Error fetching page ${pageNumber} events:`, error);
      throw new Error('Failed to fetch events');
    }
  }

  /**
   * Get total number of events
   * @returns {Promise<number>} Total event count
   */
  async getTotalEventsCount() {
    try {
      const count = await getAllElementsNumber(ElementType.EventType.elementName);
      return count || 0;
    } catch (error) {
      console.error('Error fetching event count:', error);
      throw new Error('Failed to fetch event count');
    }
  }

  /**
   * Fetch upcoming events (future events)
   * @param {number} limit - Maximum number of events to return
   * @returns {Promise<Array>} Array of upcoming event objects
   */
  async getUpcomingEvents(limit = 10) {
    try {
      const allEvents = await this.getAllEvents();
      const now = new Date();
      
      const upcomingEvents = allEvents
        .filter(event => {
          const eventDate = new Date(event.date);
          return eventDate >= now;
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(0, limit);

      return upcomingEvents;
    } catch (error) {
      console.error('Error fetching upcoming events:', error);
      throw new Error('Failed to fetch upcoming events');
    }
  }

  /**
   * Fetch past events
   * @param {number} limit - Maximum number of events to return
   * @returns {Promise<Array>} Array of past event objects
   */
  async getPastEvents(limit = 10) {
    try {
      const allEvents = await this.getAllEvents();
      const now = new Date();
      
      const pastEvents = allEvents
        .filter(event => {
          const eventDate = new Date(event.date);
          return eventDate < now;
        })
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, limit);

      return pastEvents;
    } catch (error) {
      console.error('Error fetching past events:', error);
      throw new Error('Failed to fetch past events');
    }
  }

  /**
   * Fetch related events (excluding current event)
   * @param {number} currentEventId - Current event ID to exclude
   * @param {number} limit - Number of related events to fetch (default: 3)
   * @returns {Promise<Array>} Array of related event objects
   */
  async getRelatedEvents(currentEventId, limit = 3) {
    try {
      // Fetch latest events and filter out the current one
      const events = await this.getLatestEvents(limit + 5);
      
      const relatedEvents = events
        .filter(event => event.id !== parseInt(currentEventId))
        .slice(0, limit);

      return relatedEvents;
    } catch (error) {
      console.error('Error fetching related events:', error);
      throw new Error('Failed to fetch related events');
    }
  }

  /**
   * Fetch event with related events in one call
   * @param {number} id - Event ID
   * @param {number} relatedLimit - Number of related events (default: 3)
   * @returns {Promise<Object>} Object containing event and related events
   */
  async getEventWithRelated(id, relatedLimit = 3) {
    try {
      const [event, relatedEvents] = await Promise.all([
        this.getEventById(id),
        this.getRelatedEvents(id, relatedLimit)
      ]);

      return {
        event,
        relatedEvents
      };
    } catch (error) {
      console.error(`Error fetching event ${id} with related:`, error);
      throw new Error('Failed to fetch event data');
    }
  }

  /**
   * Search events by title or location
   * @param {string} searchTerm - Search term
   * @returns {Promise<Array>} Array of matching events
   */
  async searchEvents(searchTerm) {
    try {
      if (!searchTerm || searchTerm.trim() === '') {
        return [];
      }

      const allEvents = await this.getAllEvents();
      const lowerSearchTerm = searchTerm.toLowerCase();

      return allEvents.filter(event => 
        event.title?.toLowerCase().includes(lowerSearchTerm) ||
        event.location?.toLowerCase().includes(lowerSearchTerm) ||
        event.description?.toLowerCase().includes(lowerSearchTerm)
      );
    } catch (error) {
      console.error('Error searching events:', error);
      throw new Error('Failed to search events');
    }
  }

  /**
   * Filter events by date range
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {Promise<Array>} Array of events within date range
   */
  async getEventsByDateRange(startDate, endDate) {
    try {
      const allEvents = await this.getAllEvents();
      
      return allEvents.filter(event => {
        const eventDate = new Date(event.date);
        return eventDate >= startDate && eventDate <= endDate;
      });
    } catch (error) {
      console.error('Error filtering events by date:', error);
      throw new Error('Failed to filter events');
    }
  }

  /**
   * Get event statistics
   * @returns {Promise<Object>} Statistics about events
   */
  async getEventStats() {
    try {
      const [totalCount, allEvents] = await Promise.all([
        this.getTotalEventsCount(),
        this.getAllEvents()
      ]);

      const now = new Date();
      
      const upcomingCount = allEvents.filter(event => {
        const eventDate = new Date(event.date);
        return eventDate >= now;
      }).length;

      const pastCount = totalCount - upcomingCount;

      // Group by month
      const byMonth = {};
      allEvents.forEach(event => {
        const date = new Date(event.date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        byMonth[monthKey] = (byMonth[monthKey] || 0) + 1;
      });

      return {
        total: totalCount,
        upcoming: upcomingCount,
        past: pastCount,
        byMonth,
        nextEvent: allEvents.find(event => new Date(event.date) >= now) || null
      };
    } catch (error) {
      console.error('Error fetching event stats:', error);
      throw new Error('Failed to fetch event statistics');
    }
  }

  /**
   * Get pagination info
   * @param {number} currentPage - Current page number (0-indexed)
   * @param {number} pageSize - Items per page (default: 10)
   * @returns {Promise<Object>} Pagination information
   */
  async getPaginationInfo(currentPage = 0, pageSize = 10) {
    try {
      const totalCount = await this.getTotalEventsCount();
      const totalPages = Math.ceil(totalCount / pageSize);
      
      return {
        currentPage,
        totalPages,
        totalItems: totalCount,
        pageSize,
        hasNextPage: currentPage < totalPages - 1,
        hasPreviousPage: currentPage > 0
      };
    } catch (error) {
      console.error('Error calculating pagination:', error);
      throw new Error('Failed to calculate pagination');
    }
  }
}

// Export singleton instance
const eventRepository = new EventRepository();
export default eventRepository;
